/**
 * Volume Discount Selector
 * Handles radio button selection and updates the hidden quantity input
 */
class VolumeDiscountSelector extends HTMLElement {
  constructor() {
    super();
    this.radioButtons = this.querySelectorAll('.volume-discount-radio');
    this.quantityInput = this.querySelector('.volume-discount-quantity-input');
    this.multiplierInput = this.querySelector('.volume-discount-multiplier-input');
    const multiplierContainer = this.querySelector('.volume-discount-multiplier-wrapper .m-quantity');
    this.multiplierPlusButton = multiplierContainer ? multiplierContainer.querySelector('button[name="plus"]') : null;
    this.multiplierMinusButton = multiplierContainer ? multiplierContainer.querySelector('button[name="minus"]') : null;
    this.options = this.querySelectorAll('.volume-discount-option');
    
    // Store the base quantity from the selected radio button
    this.baseQuantity = 1;
    
    // Get form ID to find the form
    const formId = this.dataset.formId;
    this.form = formId ? document.getElementById(formId) : this.closest('form');
    
    if (!this.quantityInput) {
      console.warn('Volume discount selector: quantity input not found');
      return;
    }
    
    // Find and store reference to the theme's quantity selector (m-quantity-input)
    if (this.form) {
      this.themeQuantityInput = this.form.querySelector('m-quantity-input');
      this.themeQuantityInputField = this.themeQuantityInput 
        ? this.themeQuantityInput.querySelector('input[name="quantity"]')
        : null;
      
      // Hide the theme's quantity selector visually (CSS will also handle this)
      if (this.themeQuantityInput) {
        this.themeQuantityInput.style.display = 'none';
      }
      
      // Also hide the parent container if it exists (m-product-form--input)
      const quantityContainer = this.form.querySelector('.m-product-form--input');
      if (quantityContainer && quantityContainer.querySelector('m-quantity-input')) {
        quantityContainer.style.display = 'none';
      }
    }
    
    // Add event listeners to radio buttons
    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', this.handleChange.bind(this));
    });
    
    // Add event listener to multiplier input
    if (this.multiplierInput) {
      this.multiplierInput.addEventListener('input', this.handleMultiplierChange.bind(this));
      this.multiplierInput.addEventListener('change', this.handleMultiplierChange.bind(this));
    }
    
    // Add event listeners to plus and minus buttons
    if (this.multiplierPlusButton) {
      this.multiplierPlusButton.addEventListener('click', this.handleIncrement.bind(this));
    }
    
    if (this.multiplierMinusButton) {
      this.multiplierMinusButton.addEventListener('click', this.handleDecrement.bind(this));
    }
    
    // Initialize with the checked option
    const checkedRadio = this.querySelector('.volume-discount-radio:checked');
    if (checkedRadio) {
      this.baseQuantity = parseInt(checkedRadio.value) || 1;
      this.calculateAndUpdateQuantity();
      this.updateSelectedState(checkedRadio);
    }
  }
  
  /**
   * Handles radio button change event
   * @param {Event} event - Change event
   */
  handleChange(event) {
    const radio = event.target;
    this.baseQuantity = parseInt(radio.value) || 1;
    this.calculateAndUpdateQuantity();
    this.updateSelectedState(radio);
  }
  
  /**
   * Handles multiplier input change event
   * @param {Event} event - Input/Change event
   */
  handleMultiplierChange(event) {
    // Ensure minimum value of 1
    const value = parseInt(event.target.value) || 1;
    if (value < 1) {
      event.target.value = 1;
    }
    this.calculateAndUpdateQuantity();
  }
  
  /**
   * Handles increment button click
   * @param {Event} event - Click event
   */
  handleIncrement(event) {
    event.preventDefault();
    if (this.multiplierInput) {
      const currentValue = parseInt(this.multiplierInput.value) || 1;
      this.multiplierInput.value = currentValue + 1;
      this.calculateAndUpdateQuantity();
    }
  }
  
  /**
   * Handles decrement button click
   * @param {Event} event - Click event
   */
  handleDecrement(event) {
    event.preventDefault();
    if (this.multiplierInput) {
      const currentValue = parseInt(this.multiplierInput.value) || 1;
      const newValue = Math.max(1, currentValue - 1);
      this.multiplierInput.value = newValue;
      this.calculateAndUpdateQuantity();
    }
  }
  
  /**
   * Calculates the final quantity (base quantity * multiplier) and updates the hidden input
   */
  calculateAndUpdateQuantity() {
    const multiplier = this.multiplierInput ? (parseInt(this.multiplierInput.value) || 1) : 1;
    const finalQuantity = this.baseQuantity * multiplier;
    this.updateQuantity(finalQuantity.toString());
  }
  
  /**
   * Updates the hidden quantity input value
   * @param {string} quantity - The quantity value
   */
  updateQuantity(quantity) {
    if (this.quantityInput) {
      this.quantityInput.value = quantity;
      
      // Update the theme's m-quantity-input element if it exists
      if (this.themeQuantityInputField) {
        this.themeQuantityInputField.value = quantity;
        
        // Trigger input and change events on the m-quantity-input's input field
        // This ensures the custom element and any listeners are notified
        const inputEvent = new Event('input', { bubbles: true, cancelable: true });
        const changeEvent = new Event('change', { bubbles: true, cancelable: true });
        
        this.themeQuantityInputField.dispatchEvent(inputEvent);
        this.themeQuantityInputField.dispatchEvent(changeEvent);
        
        // Also dispatch events on the m-quantity-input custom element itself
        if (this.themeQuantityInput) {
          this.themeQuantityInput.dispatchEvent(new Event('quantity-changed', { bubbles: true }));
        }
      }
      
      // Dispatch change event on our hidden input to notify other components
      const changeEvent = new Event('change', { bubbles: true });
      this.quantityInput.dispatchEvent(changeEvent);
    }
  }
  
  /**
   * Updates the visual selected state of options
   * @param {HTMLElement} selectedRadio - The selected radio button
   */
  updateSelectedState(selectedRadio) {
    // Remove selected class from all options
    this.options.forEach(option => {
      option.classList.remove('volume-discount-option--selected');
    });
    
    // Add selected class to the selected option
    const selectedOption = selectedRadio.closest('.volume-discount-option');
    if (selectedOption) {
      selectedOption.classList.add('volume-discount-option--selected');
    }
  }
}

customElements.define('volume-discount-selector', VolumeDiscountSelector);

// Also handle if the selector is already in the DOM (for dynamic content)
document.addEventListener('DOMContentLoaded', () => {
  const selectors = document.querySelectorAll('.volume-discount-selector');
  selectors.forEach(selector => {
    if (!selector.closest('volume-discount-selector')) {
      // Wrap in custom element if not already wrapped
      const wrapper = document.createElement('volume-discount-selector');
      selector.parentNode.insertBefore(wrapper, selector);
      wrapper.appendChild(selector);
    }
  });
});

